import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import SandboxBanner from './SandboxBanner';
import { sandboxApi, getSandboxToken } from './sandboxApi';

export default function SandboxValidadorDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [convocatorias, setConvocatorias] = useState([]);
  const [postulaciones, setPostulaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterConv, setFilterConv] = useState('all');
  const [filterEstado, setFilterEstado] = useState('all');
  const [reviewing, setReviewing] = useState(null);
  const [puntaje, setPuntaje] = useState(70);
  const [observaciones, setObservaciones] = useState('');

  const loadAll = useCallback(async () => {
    try {
      const [s, c, p] = await Promise.all([
        sandboxApi.stats(),
        sandboxApi.listConvocatorias(),
        sandboxApi.listPostulaciones(),
      ]);
      setStats(s.data);
      setConvocatorias(c.data);
      setPostulaciones(p.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/sandbox/validador');
      } else {
        toast.error('Error cargando datos del sandbox');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getSandboxToken()) {
      navigate('/sandbox/validador');
      return;
    }
    loadAll();
  }, [loadAll, navigate]);

  const filtered = postulaciones.filter(
    (p) =>
      (filterConv === 'all' || p.convocatoria_id === filterConv) &&
      (filterEstado === 'all' || p.estado === filterEstado)
  );

  const openReview = (p) => {
    setReviewing(p);
    setPuntaje(p.puntaje ?? 70);
    setObservaciones(p.observaciones ?? '');
  };

  const submitScore = async () => {
    if (!reviewing) return;
    try {
      await sandboxApi.scorePostulacion(reviewing.id, Number(puntaje), observaciones);
      toast.success('Calificación guardada');
      setReviewing(null);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando puntaje');
    }
  };

  const setEstado = async (estado) => {
    if (!reviewing) return;
    try {
      await sandboxApi.setEstado(reviewing.id, estado, observaciones);
      toast.success(`Marcada como ${estado}`);
      setReviewing(null);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error actualizando estado');
    }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Restablecer datos demo del sandbox?')) return;
    try {
      await sandboxApi.reset();
      toast.success('Sandbox restablecido');
      loadAll();
    } catch {
      toast.error('Error al restablecer');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SandboxBanner rolePath="validador" />
        <div className="flex items-center justify-center h-64">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SandboxBanner rolePath="validador" />
      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
              Panel Validador · Sandbox
            </h1>
            <p className="text-sm text-slate-600">
              Revisa, califica y aprueba/rechaza postulaciones de práctica.
            </p>
          </div>
          <Button
            data-testid="sandbox-reset-btn"
            variant="outline"
            onClick={handleReset}
          >
            Restablecer datos demo
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Total" value={stats?.total_postulaciones} />
          <StatCard label="Postuladas" value={stats?.postuladas} accent="text-sky-600" />
          <StatCard label="Revisadas" value={stats?.revisadas} accent="text-amber-600" />
          <StatCard label="Aprobadas" value={stats?.aprobadas} accent="text-emerald-600" />
          <StatCard label="Rechazadas" value={stats?.rechazadas} accent="text-rose-600" />
        </div>

        <Card>
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div>
              <Label className="text-xs">Convocatoria</Label>
              <Select value={filterConv} onValueChange={setFilterConv}>
                <SelectTrigger data-testid="filter-conv-select" className="w-72">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {convocatorias.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.titulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Estado</Label>
              <Select value={filterEstado} onValueChange={setFilterEstado}>
                <SelectTrigger data-testid="filter-estado-select" className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="postulada">Postulada</SelectItem>
                  <SelectItem value="revisada">Revisada</SelectItem>
                  <SelectItem value="aprobada">Aprobada</SelectItem>
                  <SelectItem value="rechazada">Rechazada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto text-sm text-slate-500">
              Mostrando {filtered.length} postulación(es)
            </div>
          </CardContent>
        </Card>

        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="px-3 py-2 text-left">Postulante</th>
                <th className="px-3 py-2 text-left">Documento</th>
                <th className="px-3 py-2 text-left">Tipo</th>
                <th className="px-3 py-2 text-left">Convocatoria</th>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-left">Puntaje</th>
                <th className="px-3 py-2 text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td className="px-3 py-6 text-center text-slate-500" colSpan={7}>
                    No hay postulaciones que coincidan con el filtro.
                  </td>
                </tr>
              )}
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`post-row-${p.id}`}
                  className="border-t hover:bg-slate-50"
                >
                  <td className="px-3 py-2 font-medium">{p.user_nombre}</td>
                  <td className="px-3 py-2 text-slate-600">{p.user_documento}</td>
                  <td className="px-3 py-2 text-slate-600">{p.user_tipo}</td>
                  <td className="px-3 py-2 text-slate-600">{p.convocatoria_titulo}</td>
                  <td className="px-3 py-2">
                    <Badge
                      className={
                        p.estado === 'aprobada'
                          ? 'bg-emerald-100 text-emerald-700'
                          : p.estado === 'rechazada'
                          ? 'bg-rose-100 text-rose-700'
                          : p.estado === 'revisada'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-sky-100 text-sky-700'
                      }
                    >
                      {p.estado}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    {p.puntaje != null ? (
                      <span className="font-semibold text-emerald-700">{p.puntaje}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      data-testid={`review-btn-${p.id}`}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => openReview(p)}
                    >
                      Revisar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Revisión de postulación</DialogTitle>
          </DialogHeader>
          {reviewing && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded p-3 text-sm">
                <div>
                  <strong>Postulante:</strong> {reviewing.user_nombre}
                </div>
                <div>
                  <strong>Documento:</strong> {reviewing.user_documento}
                </div>
                <div>
                  <strong>Tipo:</strong> {reviewing.user_tipo}
                </div>
                <div>
                  <strong>Convocatoria:</strong> {reviewing.convocatoria_titulo}
                </div>
              </div>
              <div>
                <Label htmlFor="puntaje-input">Puntaje (0-100)</Label>
                <Input
                  id="puntaje-input"
                  data-testid="puntaje-input"
                  type="number"
                  min={0}
                  max={100}
                  value={puntaje}
                  onChange={(e) => setPuntaje(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="obs-input">Observaciones</Label>
                <Textarea
                  id="obs-input"
                  data-testid="obs-input"
                  rows={3}
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Notas del validador..."
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button
              data-testid="reject-btn"
              variant="outline"
              className="text-rose-600 border-rose-300 hover:bg-rose-50"
              onClick={() => setEstado('rechazada')}
            >
              Rechazar
            </Button>
            <Button
              data-testid="mark-reviewed-btn"
              variant="outline"
              className="text-amber-700 border-amber-300 hover:bg-amber-50"
              onClick={() => setEstado('revisada')}
            >
              Marcar revisada
            </Button>
            <Button
              data-testid="approve-btn"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => setEstado('aprobada')}
            >
              Aprobar
            </Button>
            <Button
              data-testid="score-btn"
              className="bg-sky-600 hover:bg-sky-700"
              onClick={submitScore}
            >
              Guardar puntaje
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, accent = 'text-slate-900' }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
        <div className={`text-2xl font-bold ${accent}`}>{value ?? '—'}</div>
      </CardContent>
    </Card>
  );
}
